
import React, { useState } from 'react';
import { auth } from '../firebase';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { X, LogIn, UserPlus, LogOut, User, Chrome, AlertCircle } from 'lucide-react';

interface Props {
  user: any;
  onClose: () => void;
}

export const AuthModal: React.FC<Props> = ({ user, onClose }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const getErrorMessage = (errCode: string) => {
    switch (errCode) {
      case 'auth/invalid-credential':
        return 'Invalid credentials. Please check your email/password or ensure this domain is authorized in Firebase Console.';
      case 'auth/user-not-found':
        return 'No user found with this email.';
      case 'auth/wrong-password':
        return 'Incorrect password.';
      case 'auth/email-already-in-use':
        return 'This email is already registered.';
      case 'auth/popup-closed-by-user':
        return 'Sign-in popup was closed before completion.';
      default:
        return 'An error occurred. Please try again later.';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (isLogin) {
        await signInWithEmailAndPassword(auth, email, password);
      } else {
        await createUserWithEmailAndPassword(auth, email, password);
      }
      onClose();
    } catch (err: any) {
      console.error("Auth Error:", err.code, err.message);
      setError(getErrorMessage(err.code));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setLoading(true);
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
      onClose();
    } catch (err: any) {
      console.error("Google Auth Error:", err.code, err.message);
      setError(getErrorMessage(err.code));
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await signOut(auth);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[100] p-4" onClick={(e) => e.stopPropagation()}>
      <div className="bg-[var(--bg-color)] border border-sub/20 w-full max-w-md rounded-2xl shadow-2xl relative overflow-hidden animate-fadeIn">
        <button onClick={onClose} className="absolute right-4 top-4 text-sub hover:text-text transition-colors">
          <X size={20} />
        </button>

        {user ? (
          <div className="p-12 text-center">
            <div className="w-16 h-16 bg-main rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg shadow-main/20">
              <User size={32} className="text-[var(--bg-color)]" />
            </div>
            <h3 className="text-xl font-bold mb-2">Authenticated</h3>
            <p className="text-sub mb-8 truncate">{user.email}</p>
            <button 
              onClick={handleLogout}
              className="w-full flex items-center justify-center space-x-2 bg-red-500/10 hover:bg-red-500 text-red-500 hover:text-white py-3 rounded-xl transition-all font-bold"
            >
              <LogOut size={20} />
              <span>Sign Out</span>
            </button>
          </div>
        ) : (
          <div className="p-8">
            <h3 className="text-2xl font-bold mb-6 flex items-center space-x-2">
              {isLogin ? <LogIn size={24} className="text-main" /> : <UserPlus size={24} className="text-main" />}
              <span>{isLogin ? 'Sign In' : 'Register'}</span>
            </h3>

            <div className="space-y-4 mb-8">
                <button 
                    onClick={handleGoogleSignIn}
                    disabled={loading}
                    className="w-full flex items-center justify-center space-x-3 bg-white text-black hover:bg-gray-100 py-3 rounded-xl transition-all font-bold group"
                >
                    <Chrome size={20} className="group-hover:scale-110 transition-transform" />
                    <span>Continue with Google</span>
                </button>
                <div className="flex items-center space-x-2 text-sub opacity-20">
                    <div className="h-px flex-grow bg-sub"></div>
                    <span className="text-[10px] uppercase font-bold">or</span>
                    <div className="h-px flex-grow bg-sub"></div>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-xs uppercase font-bold text-sub mb-1 block">Email</label>
                <input 
                  type="email" required value={email} onChange={e => setEmail(e.target.value)}
                  className="w-full bg-sub/5 border border-sub/20 focus:border-main outline-none p-3 rounded-xl transition-all text-text"
                  placeholder="email@example.com"
                />
              </div>
              <div>
                <label className="text-xs uppercase font-bold text-sub mb-1 block">Password</label>
                <input 
                  type="password" required value={password} onChange={e => setPassword(e.target.value)}
                  className="w-full bg-sub/5 border border-sub/20 focus:border-main outline-none p-3 rounded-xl transition-all text-text"
                  placeholder="••••••••"
                />
              </div>

              {error && (
                <div className="flex items-start space-x-2 text-red-500 text-xs mt-2 font-bold bg-red-500/10 p-3 rounded-lg border border-red-500/20">
                  <AlertCircle size={16} className="shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <button 
                disabled={loading}
                className="w-full bg-main text-[var(--bg-color)] py-3 rounded-xl font-bold hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50"
              >
                {loading ? 'Wait...' : isLogin ? 'Sign In' : 'Create Account'}
              </button>
            </form>

            <button 
              onClick={() => setIsLogin(!isLogin)}
              className="w-full mt-6 text-sub text-xs hover:text-text transition-colors"
            >
              {isLogin ? "Need an account? Register" : "Have an account? Login"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
